// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import Authentication from '@app/features/auth/state/Authentication';
import styles from '@app/features/channel/components/modals/GroupInvitesModal.module.css';
import Channels from '@app/features/channel/state/Channels';
import * as InviteCommands from '@app/features/invite/commands/InviteCommands';
import {InviteRevokeFailedModal} from '@app/features/invite/components/alerts/InviteRevokeFailedModal';
import {InvitesLoadFailedModal} from '@app/features/invite/components/alerts/InvitesLoadFailedModal';
import {InviteDateToggle} from '@app/features/invite/components/InviteDateToggle';
import {InviteListHeader, InviteListItem} from '@app/features/invite/components/InviteListItem';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Scroller} from '@app/features/ui/components/Scroller';
import {Spinner} from '@app/features/ui/components/Spinner';
import type {Invite} from '@app/vendor/schema/domains/invite/InviteSchemas';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useState} from 'react';

const logger = new Logger('GroupInvitesModal');
export const GroupInvitesModal = observer(({channelId}: {channelId: string}) => {
	const {t} = useLingui();
	const channel = Channels.getChannel(channelId);
	const isOwner = channel?.ownerId === Authentication.currentUserId;
	const [invites, setInvites] = useState<Array<Invite>>([]);
	const [fetchStatus, setFetchStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
	const [showCreatedDate, setShowCreatedDate] = useState(false);
	const loadInvites = useCallback(async () => {
		if (!isOwner) return;
		try {
			setFetchStatus('pending');
			const data = await InviteCommands.list(channelId);
			setInvites(data);
			setFetchStatus('success');
		} catch (error) {
			logger.error('Failed to load invites:', error);
			setFetchStatus('error');
			ModalCommands.push(modal(() => <InvitesLoadFailedModal />));
		}
	}, [channelId, isOwner]);
	useEffect(() => {
		if (isOwner && fetchStatus === 'idle') {
			loadInvites();
		}
	}, [fetchStatus, loadInvites, isOwner]);
	const handleRevoke = useCallback(
		(code: string) => {
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Revoke Invite`}
						description={t`Are you sure you want to revoke this invite? This action cannot be undone.`}
						primaryText={t`Revoke`}
						onPrimary={async () => {
							try {
								await InviteCommands.remove(code);
								ToastCommands.createToast({
									type: 'success',
									children: <Trans>Invite revoked</Trans>,
								});
								await loadInvites();
							} catch (error) {
								logger.error('Failed to revoke invite:', error);
								window.setTimeout(() => {
									ModalCommands.push(modal(() => <InviteRevokeFailedModal />));
								}, 0);
							}
						}}
					/>
				)),
			);
		},
		[loadInvites],
	);
	if (!isOwner) {
		return (
			<Modal.Root className={styles.modalRoot}>
				<Modal.Header title={t`Group Invites`} />
				<Modal.Content>
					<div className={styles.container}>
						<div className={styles.errorBox}>
							<p className={styles.errorText}>
								<Trans>Only the group owner can manage invites.</Trans>
							</p>
						</div>
					</div>
				</Modal.Content>
			</Modal.Root>
		);
	}
	return (
		<Modal.Root className={styles.modalRoot}>
			<Modal.Header title={t`Group Invites`} />
			<Modal.Content>
				<div className={styles.container}>
					{fetchStatus === 'pending' && (
						<div className={styles.spinnerContainer}>
							<Spinner />
						</div>
					)}
					{fetchStatus === 'error' && (
						<div className={styles.errorBox}>
							<p className={styles.errorText}>
								<Trans>Failed to load invites. Please try again.</Trans>
							</p>
						</div>
					)}
					{fetchStatus === 'success' && invites.length === 0 && (
						<div className={styles.stateBox}>
							<p className={styles.stateText}>
								<Trans>No invites created</Trans>
							</p>
						</div>
					)}
					{fetchStatus === 'success' && invites.length > 0 && (
						<div className={styles.invitesWrapper}>
							<InviteDateToggle showCreatedDate={showCreatedDate} onToggle={setShowCreatedDate} />
							<div className={styles.invitesList}>
								<Scroller className={styles.scroller} key="group-invites-scroller">
									<InviteListHeader showCreatedDate={showCreatedDate} />
									<div className={styles.inviteItems}>
										{invites.map((invite) => (
											<InviteListItem
												key={invite.code}
												invite={invite}
												onRevoke={handleRevoke}
												showCreatedDate={showCreatedDate}
											/>
										))}
									</div>
								</Scroller>
							</div>
						</div>
					)}
				</div>
			</Modal.Content>
		</Modal.Root>
	);
});
